import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import Cropper from 'cropperjs';
import { DialogIncludesTypes } from '@app/core/models/diallog';
import { NotificationService } from '@app/modules/notification-modules/notification.service';
import { PerformersService } from '@app/modules/account-modules/performers.service';
import { FileToBase64 } from '@app/utils/converter';
import { MainPhotoEditDialogData } from './main-photo-edit-dialog.model';

const MAX_FILE_MB = 1;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
const CROPPED_MAX_SIZE = 1200;
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

@UntilDestroy()
@Component({
  selector: 'app-main-photo-edit-dialog',
  templateUrl: './main-photo-edit-dialog.component.html',
  styleUrls: ['./main-photo-edit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MainPhotoEditDialogComponent implements OnDestroy {
  @ViewChild('cropImage', { static: false }) cropImageRef: ElementRef<HTMLImageElement>;

  preview: string | null = null;
  key: string | null = null;
  selectedFile: File | null = null;
  loading = false;
  applyLoading = false;
  performerId: string | null = null;
  /** Show cropper (only when performerId and image loaded). */
  showCropper = false;

  private cropper: Cropper | null = null;

  get isApplyEnabled(): boolean {
    if (this.applyLoading) return false;
    if (this.showCropper) return !!this.cropper;
    return !!this.selectedFile;
  }

  constructor(
    public ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private performersService: PerformersService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef
  ) {
    const data = (this.config.data || {}) as MainPhotoEditDialogData;
    this.preview = data.preview ?? null;
    this.key = data.key ?? null;
    this.performerId = data.performerId ?? null;
  }

  ngOnDestroy(): void {
    this.destroyCropper();
  }

  onFileChange(files: FileList, inputEl: HTMLInputElement): void {
    if (!files?.length) return;
    const file = files[0];
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      this.notificationService.onDialog({
        type: DialogIncludesTypes.PopupErrorTemplate,
        header: 'Ошибка',
        content: 'Допустимые форматы: PNG, JPG, JPEG.'
      });
      if (inputEl) inputEl.value = '';
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      this.notificationService.onDialog({
        type: DialogIncludesTypes.PopupErrorTemplate,
        header: 'Ошибка',
        content: `Размер файла не должен превышать ${MAX_FILE_MB} Мб.`
      });
      return;
    }
    this.destroyCropper();
    this.selectedFile = file;
    this.loading = !this.performerId;
    this.showCropper = false;
    this.cdr.markForCheck();
    FileToBase64(file).then((dataUrl: unknown) => {
      this.preview = (dataUrl as string) || null;
      if (this.performerId) {
        this.showCropper = true;
        this.loading = false;
      }
      this.cdr.markForCheck();
    });
    if (this.performerId) {
      if (inputEl) inputEl.value = '';
      return;
    }
    this.performersService.uploadPhotoToBuffer(file).pipe(untilDestroyed(this)).subscribe(
      k => {
        this.key = k || null;
        this.loading = false;
        if (inputEl) inputEl.value = '';
        this.cdr.markForCheck();
      },
      err => {
        this.preview = null;
        this.loading = false;
        this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupErrorTemplate,
          header: 'Ошибка загрузки',
          content: (err && (err.message || err.error)) || 'Не удалось загрузить фото.'
        });
        if (inputEl) inputEl.value = '';
        this.cdr.markForCheck();
      }
    );
  }

  onCropImageLoad(): void {
    if (!this.performerId || !this.showCropper || !this.cropImageRef?.nativeElement) return;
    this.destroyCropper();
    const img = this.cropImageRef.nativeElement;
    requestAnimationFrame(() => {
      if (!this.cropImageRef?.nativeElement) return;
      this.cropper = new Cropper(this.cropImageRef.nativeElement, {
        viewMode: 1,
        dragMode: 'crop',
        movable: false,
        autoCropArea: 0.9,
        restore: false,
        guides: true,
        center: true,
        highlight: true,
        cropBoxMovable: true,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false
      });
      this.cdr.markForCheck();
    });
  }

  private destroyCropper(): void {
    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = null;
    }
  }

  remove(): void {
    this.destroyCropper();
    this.preview = null;
    this.key = null;
    this.selectedFile = null;
    this.showCropper = false;
    this.cdr.markForCheck();
  }

  onApply(): void {
    if (!this.performerId) return;
    if (this.cropper) {
      this.applyLoading = true;
      this.cdr.markForCheck();
      const canvas = this.cropper.getCroppedCanvas({
        maxWidth: CROPPED_MAX_SIZE,
        maxHeight: CROPPED_MAX_SIZE,
        imageSmoothingQuality: 'high'
      });
      if (!canvas) {
        this.applyLoading = false;
        this.cdr.markForCheck();
        return;
      }
      canvas.toBlob(
        blob => {
          if (!blob) {
            this.applyLoading = false;
            this.cdr.markForCheck();
            return;
          }
          const name = this.selectedFile?.name || 'photo.jpg';
          const safeName = /\.(jpe?g|png)$/i.test(name) ? name : name.replace(/\.[^.]+$/i, '') + '.jpg';
          const file = new File([blob], safeName, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });
          this.performersService.updateMainPhoto(this.performerId!, file).pipe(untilDestroyed(this)).subscribe(
            () => {
              this.applyLoading = false;
              this.ref.close({ applied: true });
              this.cdr.markForCheck();
            },
            err => {
              this.applyLoading = false;
              this.notificationService.onDialog({
                type: DialogIncludesTypes.PopupErrorTemplate,
                header: 'Ошибка',
                content: (err && (err.message || err.error)) || 'Не удалось обновить фото.'
              });
              this.cdr.markForCheck();
            }
          );
        },
        'image/jpeg',
        0.92
      );
      return;
    }
    if (!this.selectedFile) return;
    this.applyLoading = true;
    this.cdr.markForCheck();
    this.performersService.updateMainPhoto(this.performerId, this.selectedFile).pipe(untilDestroyed(this)).subscribe(
      () => {
        this.applyLoading = false;
        this.ref.close({ applied: true });
        this.cdr.markForCheck();
      },
      err => {
        this.applyLoading = false;
        this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupErrorTemplate,
          header: 'Ошибка',
          content: (err && (err.message || err.error)) || 'Не удалось обновить фото.'
        });
        this.cdr.markForCheck();
      }
    );
  }

  onDone(): void {
    this.ref.close({ preview: this.preview, key: this.key });
  }

  onCancel(): void {
    this.ref.close();
  }
}
